import { Card, CardBody, CardFooter, CardHeader, Col, Container, Row } from 'reactstrap';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';

import MonitoringBait from '../VisitReports/MonitoringBait';

import Monitoring from './Monitoring';
import { TrapTimeLineChart } from './Charts/TrapsTimeLineChart';
import { SBSChart } from './Charts/SBSChart';

import { MLButton } from 'components/Button/Button';
import { getVisitReports } from 'api/visitReports/getVisitReports';
import useStartVisitTimer from 'hooks/VisitReport/useStartVisit';
import useEndVisitTimer from 'hooks/VisitReport/useEndVisit';
import useGetUser from 'hooks/VisitReport/useGetUser';
import EncounterAccordion from 'components/PeriodReportAccordions/EncounterAccordion/EncounterAccordion';
import TrapsDataAccordion from 'components/PeriodReportAccordions/TrapsData/TrapsDataAccordion';
import VulnerabilityAccordion from 'components/PeriodReportAccordions/VulnerabilityAccordion/VulnerabilityAccordion';
import UsedPoisonsAccordion from 'components/PeriodReportAccordions/UsedPoisonsAccordion/UsedPoisonsAccordion';
import { TrapType } from 'components/AnalyticsPage/TrapType';

const VisitReport = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  // Створення POST # 4 " Ініціалізую хук значення в {} можна отримати ctrl + space " | Start Block |

  const { startVisitTimer } = useStartVisitTimer();

  // Створення POST # 4 " Ініціалізую хук значення в {} можна отримати ctrl + space " | End Block |
  const { endVisitTimer } = useEndVisitTimer();
  const { data } = useQuery('visitDocs', () => getVisitReports({ slug: slug || '', id: id || '' }), {});
  const { data: userData } = useGetUser();
  let { slug, id } = useParams();

  return (
    <div className="page-content bg-light">
      <Container fluid>
        <Card>
          <CardHeader>
            <Row className="align-items-center">
              <Col lg={9}>
                <div className="text-start">
                  <h4 className="card-title mb-0 fs-20">
                    {t('VisitReports.page-header.title-1')} {data?.start_date} {t('VisitReports.page-header.title-2')}{' '}
                    {data?.end_date}
                  </h4>
                </div>
              </Col>
              <Col lg={3} className="d-flex justify-content-end">
                <MLButton
                  innerText={t('VisitReports.page-header.pdf-button')}
                  color="purple"
                  fontSize={18}
                  icon="ri-file-pdf-fill fs-16"
                  isGhost
                  onClick={() => {
                    navigate(`/visit-report/${slug}/${id}/check/`);
                  }}
                />
              </Col>
            </Row>
          </CardHeader>
          <CardBody>
            <div className="row border-bottom-dashed border-light my-3" />
            {data?.start_date && data?.end_date && (
              <TrapTimeLineChart
                slug={slug || ''}
                trapType={TrapType.MCT}
                startDate={data.start_date}
                endDate={data.end_date}
              />
            )}
            <div className="row border-bottom-dashed border-light my-3" />
            <EncounterAccordion encounters={data?.enc_data || []} />
            <TrapsDataAccordion trap_data_list={data?.traps_data || []} />
            <VulnerabilityAccordion vul_data_list={data?.vuls_data || []} />
            <UsedPoisonsAccordion poisons={data?.poison_usage || []} id={id} slug={slug} />
            <Monitoring monitoring_data={data?.monitoring_data || []} />
            <MonitoringBait monitoring_bait_data={data?.monitoring_bait_data || []} slug={slug} />

            <div className="row border-bottom-dashed border-light my-3" />
            {data?.start_date && data?.end_date && (
              <SBSChart slug={slug || ''} startDate={data.start_date} endDate={data.end_date} />
            )}
            <div className="row border-bottom-dashed border-light my-3" />
          </CardBody>
          <CardFooter>
            {userData && ['pest_client_employee', 'pest_client_manager'].includes(userData.role) ? (
              <div className="row">
                {data && data.timer_start === null && data.started_timer_by === null ? (
                  <MLButton
                    innerText={t('VisitReports.page-header.start-timer-button')}
                    color="info"
                    fontSize={18}
                    icon="ri-add-line fs-16"
                    onClick={() => {
                      // Створення POST # 5 " Якщо немає форми можу просто викликати функцію " | Start Block |
                      startVisitTimer({
                        startVisitData: { timer_start: null, started_timer_by: null },
                        id: id || '',
                        slug: slug || '',
                      });
                      // Створення POST # 5 " Якщо немає форми можу просто викликати функцію " | End Block |
                    }}
                  />
                ) : data && data.timer_end === null && data.ended_timer_by === null ? (
                  <>
                    <div className="col-md-6">
                      <h6>
                        {t('VisitReports.page-header.timer-data.visit-start-time')}{' '}
                        {dayjs(data.timer_start).format('YYYY-MM-DD\xa0\xa0\xa0HH:mm')} by {data.started_timer_by}.
                      </h6>
                    </div>
                    <div className="col-md-6">
                      <MLButton
                        innerText={t('VisitReports.page-header.end-timer-button')}
                        color="info"
                        fontSize={18}
                        icon="ri-add-line fs-16"
                        onClick={() => {
                          endVisitTimer({
                            endVisitData: { timer_end: null, ended_timer_by: null },
                            id: id || '',
                            slug: slug || '',
                          });
                        }}
                      />
                    </div>
                  </>
                ) : data ? (
                  <>
                    <div className="col-md-6">
                      <h6>
                        {t('VisitReports.page-header.timer-data.visit-start-time')}{' '}
                        {dayjs(data.timer_start).format('YYYY-MM-DD\xa0\xa0\xa0HH:mm')}{' '}
                        {t('VisitReports.page-header.timer-data.visit-start-end-by')} {data.started_timer_by}.
                      </h6>
                    </div>
                    <div className="col-md-6">
                      <h6>
                        {t('VisitReports.page-header.timer-data.visit-end-time')}{' '}
                        {dayjs(data.timer_end).format('YYYY-MM-DD\xa0\xa0\xa0HH:mm')}{' '}
                        {t('VisitReports.page-header.timer-data.visit-start-end-by')} {data.ended_timer_by}.
                      </h6>
                    </div>
                  </>
                ) : (
                  <h4>Data not available</h4>
                )}
              </div>
            ) : null}
          </CardFooter>
        </Card>
      </Container>
    </div>
  );
};

export default VisitReport;
